import * as amplitude from "@amplitude/analytics-browser";
import * as Sentry from "@sentry/browser";
import { useLDClient } from "launchdarkly-react-client-sdk";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import type { WorkspaceMembership } from "@storyboard/web-client";
import type { Device, UserInfoResponse } from "@storyboard/web-client/auth";
import ElectronComponentLoader from "@/components/electron/ElectronComponentLoader.tsx";
import ElectronNotifications from "@/components/electron/ElectronNotifications.tsx";
import InvalidWorkspaceModal from "@/components/InvalidWorkspaceModal";
import LoadingScreen from "@/components/LoadingScreen.tsx";
import NoValidWorkspacesModal from "@/components/NoValidWorkspacesModal.tsx";
import { ampli } from "@/config";
import { config } from "@/config.ts";
import { useWorkspaceConfig } from "@/hooks/useWorkspaceConfig";
import { identifyLaunchDarklyUser } from "@/init/identifyLaunchDarklyUser";
import { createLogger } from "@/logger";
import { LANGUAGE_LIST, type PreferredLanguage } from "@/models/languages";
import { bulkUpsertAccount, setPreferredLanguage, upsertMyAccount } from "@/models/repositories/Account";
import { resetAllItemsLoadedContent } from "@/models/repositories/Item";
import { bulkUpsertWorkspace } from "@/models/repositories/Workspace";
import { bulkUpsertWorkspaceMembership } from "@/models/repositories/WorkspaceMembership";
import { AuthContext } from "@/models/StateProviders/AuthProvider.tsx";
import { useTelemetry } from "@/models/TelemetryProvider.tsx";
import { ResetCoordinator } from "@/services/ResetCoordinator";
import { rowsWhere, store } from "@/store/store";
import { ipcRendererCallback, ipcRendererSend, isElectron, removeIpcListener } from "@/utils";

const log = createLogger("feed");

/** The language the picker last stored, or null when storage holds none or holds a value the picker never offers. */
function readStoredLanguage(): PreferredLanguage | null {
  try {
    const raw = window.localStorage.getItem("preferredLanguage");
    const language = raw ? (JSON.parse(raw) as PreferredLanguage) : null;
    return language && LANGUAGE_LIST.includes(language) ? language : null;
  } catch {
    return null;
  }
}

type UserState = {
  user?: UserInfoResponse | null;
  workspaceId?: string | null;
  fetchUserDevice?: (token: string) => Device | null;
  workspaceMembership?: WorkspaceMembership | null;
  tokens?: string[];
  appVersion?: string;
  dispatcherFunction: string;
  setDispatcherFunction?: (value: string) => void;
};
const defaultAppVersion = "0.0.0";
export const UserContext = createContext<UserState>({
  user: null,
  workspaceId: null,
  fetchUserDevice: undefined,
  workspaceMembership: null,
  tokens: [],
  appVersion: defaultAppVersion,
  dispatcherFunction: "",
});
const UserProvider = ({ children }: { children: React.ReactNode }) => {
  /**
   *  User is retrieved from AuthContext
   */

  const { setAttribute } = useTelemetry();
  const { user } = useContext(AuthContext);
  const { workspaceId } = useParams();
  const workspaceConfig = useWorkspaceConfig(workspaceId);
  const ldClient = useLDClient();
  const [currentUserWorkspaceId, setCurrentUserWorkspaceId] = useState<string | null>(workspaceId);
  const [appVersion, setAppVersion] = useState<string>(defaultAppVersion);
  const workspaceMembership = user?.workspaceMemberships?.find(
    (membership) => membership.workspaceId === currentUserWorkspaceId,
  );
  const [dispatcherFunction, setDispatcherFunction] = useState<string>(
    workspaceMembership?.config?.dispatcherFunction ?? "",
  );

  useEffect(() => {
    if (workspaceId && user?.workspaces?.length > 0 && user?.workspaces?.some((ws) => ws.id === workspaceId)) {
      setCurrentUserWorkspaceId(workspaceId);
    }
  }, [workspaceId, user]);

  useEffect(() => {
    const preferred = workspaceMembership?.config?.dispatcherFunction ?? workspaceConfig?.dispatcherFunction;
    if (preferred) {
      setDispatcherFunction(preferred);
    }
  }, [workspaceMembership?.config?.dispatcherFunction, workspaceConfig?.dispatcherFunction]);

  const identifyEventRef = useRef<amplitude.Identify>(new amplitude.Identify());

  const updateUserAccounts = async (userData: UserInfoResponse): Promise<void> => {
    const workspaceId = userData?.workspaceMemberships?.[0]?.workspaceId;
    // TODO: Confirm if this is the reason we see the "wrong workspace" error
    // whenever we login as a new user with a new workspace without logging out first
    if (!currentUserWorkspaceId && workspaceId) {
      setCurrentUserWorkspaceId(() => workspaceId);
    }
    await bulkUpsertWorkspace(userData?.workspaces || []);

    if (userData?.workspaceMemberships?.length > 0) {
      await bulkUpsertWorkspaceMembership(userData?.workspaceMemberships || []);
    }

    if (userData?.accounts?.length > 0) {
      await bulkUpsertAccount(userData?.accounts || []);
    }

    await upsertMyAccount(userData);

    // The PGlite-era code looked like it seeded `preferredLanguage` here, but its guard tested the
    // imported Drizzle table object (always truthy) so the write never ran: accounts kept a null
    // preferredLanguage and readers applied their own fallbacks (DriverView → "en"). Porting the
    // *intended* logic stamped "none" and broke every transcription lookup keyed on the account
    // language (empty "Most Recent Driver Message" column). Keep the effective no-op, and clear
    // the bad value for sessions whose snapshot was already stamped — "none" is never a
    // user-selectable value, so it can only come from that bug.
    const accountRecord = rowsWhere("account", (row) => row.mine)[0];
    if (accountRecord?.id && accountRecord.preferredLanguage === "none") {
      store.update("account", accountRecord.id, (row) => ({ ...row, preferredLanguage: null }));
    }
    // The language picker writes both the account row and localStorage, but a cache clear drops the snapshot and with
    // it the account row's language, while localStorage keeps the choice. Carry the stored choice back onto the row so
    // the Dispatch view, which reads the row, reads the same language as the picker and the sync, which read storage.
    const storedLanguage = readStoredLanguage();
    if (accountRecord?.id && storedLanguage && accountRecord.preferredLanguage !== storedLanguage) {
      // As the picker does: content an item loaded under the other language is not loaded for this one.
      await resetAllItemsLoadedContent();
      setPreferredLanguage(accountRecord.id, storedLanguage);
    }

    const buildVersion = config.releaseTag;
    identifyEventRef.current.set("buildVersion", buildVersion);
    amplitude.identify(identifyEventRef.current);
    ampli.authSuccess({ "Auth0 App": "SMS" });
    if (accountRecord?.id) {
      const identifyPayload = {
        kind: "user",
        key: accountRecord.id,
        accountId: accountRecord.id,
        name: accountRecord?.name,
        email: accountRecord?.email,
        sbAccount: accountRecord?.email?.includes("@storyboard.fm"),
        workspaceId: workspaceId,
        anonymous: false,
        buildVersion,
      };
      Sentry.setUser({
        id: accountRecord.id,
        email: accountRecord?.email,
        username: accountRecord?.name,
        workspaceId: workspaceId,
        buildVersion,
        sbAccount: accountRecord?.email?.includes("@storyboard.fm"),
      });
      ampli.client.setUserId(accountRecord.id);

      if (ldClient) {
        await identifyLaunchDarklyUser(ldClient, identifyPayload, accountRecord.id);
      } else {
        log.warn("LaunchDarkly client not available for user identification");
      }

      ResetCoordinator.getInstance().updateUserMetadata({
        accountId: accountRecord.id,
        workspaceId,
        workspaceMembershipId: workspaceMembership?.id,
      });
    } else {
      log.warn("No Account ID");
    }
  };

  const setVersionCallback = useCallback(
    (_: any, value: string) => {
      if (!isElectron) return;
      setAttribute("electronVersion", value);

      // Create a new Identify object to avoid property accumulation
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("electronVersion", value);
      amplitude.identify(identifyEvent);

      setAppVersion(() => value);
    },
    [setAttribute, isElectron],
  );

  useEffect(() => {
    if (user?.accounts?.length > 0) {
      updateUserAccounts(user);
    }
  }, [user]);

  useEffect(() => {
    ipcRendererSend("fetch-version");
    ipcRendererCallback("set-version", setVersionCallback);
    return () => {
      removeIpcListener("set-version", setVersionCallback);
    };
  }, [setVersionCallback]);

  const tokens = user?.devices?.map((device) => device.deviceToken);
  const fetchUserDevice = (token: string): Device | null => {
    return user?.devices?.find((device) => device.deviceToken === token && device.enabled) || null;
  };

  const invalidWorkspace =
    currentUserWorkspaceId &&
    user?.workspaces?.length > 0 &&
    !user?.workspaces?.find((ws) => ws.id === currentUserWorkspaceId);

  const defaultState = (
    <>
      {user?.accounts?.length > 0 && (
        <>
          <ElectronComponentLoader component={<ElectronNotifications tokens={tokens} user={user} />} />
        </>
      )}
      {children}
    </>
  );

  const workspaceIdIsNull = workspaceId === null || workspaceId === "null";

  return (
    <UserContext.Provider
      value={{
        user,
        workspaceId: currentUserWorkspaceId,
        workspaceMembership,
        tokens,
        fetchUserDevice,
        appVersion,
        dispatcherFunction,
        setDispatcherFunction,
      }}
    >
      {invalidWorkspace && !workspaceIdIsNull && (
        <InvalidWorkspaceModal open={true} allowClose={false} workspaceId={workspaceId} />
      )}
      {(!user || (user && !workspaceId)) && (
        <>
          <LoadingScreen />
        </>
      )}
      {((user && !user?.workspaces?.length) || workspaceIdIsNull) && <NoValidWorkspacesModal open={true} />}
      {user && user?.workspaces?.length && defaultState}
    </UserContext.Provider>
  );
};

export default UserProvider;
